<script setup>
import {ref, computed, onMounted, watch, reactive, readonly} from "vue";
import { useI18n } from "vue-i18n";
import { handleError } from '@/utils/errors';
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import { useRouter } from "vue-router";
import { computeClass } from "@/utils/styleFormats.js";
import BaseTable from "@/components/tables/BaseTable.vue";
import {AttributeEntity} from "@/entities/AttributeEntity.js";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";
import BaseAdvancedFilter from "@/components/common/Filters/BaseAdvancedFilter.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import {deleteAttribute, getAttribute} from "@/services/api/attribute.js";
import {getAvailableActionsForRoute} from "@/services/api/permissionManagement.js";
import {searchAndSetOptions} from "@/utils/index.js";
import {getManufacturer} from "@/services/api/manufacturer.js";

const { t } = useI18n();
const router = useRouter();
const attributeEntity = new AttributeEntity();
const entity = ref(attributeEntity);

const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const filters = ref([]);
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);
const actions = ref([]);
const havePermission = ref(false);
const showDialogConfirmDelet = ref(false);
const itemToDelete = ref(null)
const isFetching = ref(false);
const orderBy = ref(null);

async function fetchData(params = {}) {
    try {
        loading.value = true;
        currentPage.value = params.page ?? currentPage.value;
        if (orderBy.value) {
            params['orderBy'] = orderBy.value.key;
        }
        for (const key in params) {
            const value = params[key];

            if (
                value &&
                typeof value === 'object' &&
                'title' in value &&
                'value' in value
            ) {
                params[key] = value.title;
            }
        }

        if (searchText.value) {
            const field = quickSearchConfig.value.searchBy[0];
            params[field] = "%" + searchText.value + "%";
        }

        if (filters.value && Object.keys(filters.value).length > 0) {
            params = {
                ...filters.value,
                ...params
            };
        }

        const response = await getAttribute({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...params
        });

        items.value = response.data.items ?? response.data;
        if (Array.isArray(items.value)) {
            items.value = items.value.map(item => ({
                ...item,
                manufacturer: item.manufacturer?.name || item.manufacturer || null,
            }));
        } else if (items.value && typeof items.value === "object") {
            items.value = {
                ...items.value,
                manufacturer: items.value.manufacturer?.name || items.value.manufacturer || null,
            };
        }

        totalItems.value = response.page;
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

function onEdit(item) {
    router.push(entity.value.getUrl(entity.value.pages.edit.route?.at(-1), { id: item.id }));
}

async function onPageChange(page) {
    await fetchData({page: page});
}

async function onDelete(item) {
    itemToDelete.value = item;
    showDialogConfirmDelet.value = true;
}

async function onAdvancedFilter(filtersAdvanced) {
    filters.value = filtersAdvanced;
    await fetchData(filtersAdvanced);
}

async function onCleanFilter() {
    filters.value = null;
    await fetchData();
}

async function onOrderBy(orderByArray) {
    orderBy.value = orderByArray;
}

const confirmDelete = async () => {
    try {
        await deleteAttribute({id: itemToDelete.value.id});
        await fetchData();
        message.success(t('common.messages.success.itemDeletedSuccessfully'));
        showDialogConfirmDelet.value = false;
    } catch (error) {
        if(error.response.data.error === "Attribute is being used by some layout.") {
            message.error(t('common.messages.errors.attributeCannotBeDeletedBecauseUsedByLayout'));
        } else {
            message.error(await handleError(error, t));
        }
    }
};

async function handleSearch({ handler, query }) {
    const handlers = {
        handleSearchManufacturer: () =>
            searchAndSetOptions({
                key: "manufacturer_name",
                apiFn: getManufacturer,
                query,
                entity,
                advancedFilter: true,
                isFetching
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}

onMounted(async () => {
    actions.value = await getAvailableActionsForRoute(entity.value.APIRoute);

    if (!actions.value?.list?.length) {
        message.error(t("common.messages.errors.403"));
    } else {
        havePermission.value = true;
        await fetchData();
    }
})
</script>

<template>
    <BasePage :entity="entity">
        <template #default>
            <v-row class="mb-4">
                <v-col>
                    <ButtonNew  :route="entity.pages.create.route?.at(-1)" v-if="actions && actions.create" />
                </v-col>
                <v-col class="base-advanced-filter">
                    <BaseAdvancedFilter
                        :entity="entity"
                        @search="handleSearch"
                        @filter="onAdvancedFilter"
                        @clean="onCleanFilter"
                    />
                </v-col>
                <v-col class="base-quick-search">
                    <BaseQuickSearch
                        :entity="entity"
                        v-if="quickSearchConfig"
                        :config="quickSearchConfig"
                        v-model="searchText"
                        :fetchData="fetchData"
                    />
                </v-col>
            </v-row>

            <BaseTable
                :fetchData="fetchData"
                :entity="entity"
                :items="items"
                :loading="loading"
                :totalItems="totalItems"
                :currentPage="currentPage"
                :itemsPerPage="itemsPerPage"
                :actions="actions"
                :tooltipText="t('common.messages.info.defaultCannotbeChanged', {name: t(entity.entityName)})"
                @edit="onEdit"
                @delete="onDelete"
                @page-change="onPageChange"
                @onOrderBy="onOrderBy"
            >
                <template #status="{ row }">
                    <span class="trezo-status-badge" :class="computeClass(row.status)" >
                        {{ t('common.labels.' + (row.status ? 'active' : 'inactive' )) }}
                    </span>
                </template>
            </BaseTable>

            <DialogConfirmDelet
                v-model="showDialogConfirmDelet"
                :item="itemToDelete?.name"
                @confirm="confirmDelete"
            />

        </template>
    </BasePage>
</template>
